import React, { useContext, useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from 'react-i18next';
import { BreadcrumbContext } from '../../../../components/Breadcrumb';
import { AuthContext } from "../../../../components/FirebaseAuth";
import { FirebaseAuth } from "../../../../components/FirebaseAuth/firebase";
import { useNavigate } from "react-router-dom";
import Loader from "../../../../components/Loader";
import DataTable from "../../../../components/DataTable";
import currencyJSON from "../../../../inc/currency.json";
import { Box, Stack, Button, Alert, Card, Typography } from "@mui/material";

const PaymentList = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const currency = currencyJSON.currency

    const { userData, authUser } = useContext(AuthContext);
    const { setBreadcrumb } = useContext(BreadcrumbContext);

    // document snapshots
    const [qs, setQs] = useState(null);
    const mountedRef = useRef(true);
    const [total, setTotal] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [page, setPage] = useState(0);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const getInvoices = useCallback((accountId, pageSize, direction, doc) => {
        const getInvoiceCollectionCount = (accountId) => {
            const accountDocRef = FirebaseAuth.firestore().collection('accounts').doc(accountId);
            return accountDocRef.get().then(accountDoc => {
                if(accountDoc.exists){
                    return accountDoc.data().invoicesColCount;
                }else{
                    return 0;
                }
            }).catch(() => {
                return 0;
            })
        }

        setLoading(true);
        let records = [];
        const collectionRef = FirebaseAuth.firestore().collection('accounts').doc(accountId).collection('invoices');
        let query = collectionRef.orderBy('created', 'desc');
        if(direction && direction === 'next'){
            query = query.startAfter(doc);
        }
        if(direction && direction === 'previous'){
            query = query.endBefore(doc);
        }
        query = query.limit(pageSize);
        Promise.all([getInvoiceCollectionCount(accountId), query.get()]).then(([invoiceCount, documentSnapshots]) => {
            if (!mountedRef.current) return null
            setTotal(invoiceCount);
            documentSnapshots.forEach(doc => {
                records.push({
                    'id': doc.id,
                    'total': (doc.data().total / 100).toFixed(2),
                    'subTotal': (doc.data().subTotal / 100).toFixed(2),
                    'tax': ((doc.data().tax || 0) / 100).toFixed(2),
                    'amountPaid': (Math.round(doc.data().amountPaid / 100)).toFixed(2),
                    'created': (new Date(doc.data().created * 1000)).toLocaleString(),
                    'hostedInvoiceUrl': doc.data().hostedInvoiceUrl,
                    'currency': doc.data().currency,
                    'status': doc.data().status,
                    'amountCol': <>{currency[doc.data().currency].sign}{(doc.data().total / 100).toFixed(2)}</>,
                    'statusCol': <>{doc.data().status.toUpperCase()}</>,
                    'urlCol': doc.data().hostedInvoiceUrl?(
                        <Button href={doc.data().hostedInvoiceUrl} rel="noreferrer" target="_blank" variant="contained" size="small">{t('billing.viewInvoice')}</Button>
                    ):(<></>)
                });
            });

            if(records.length > 0){
                setRows(records);
                setQs(documentSnapshots);
            }
            setLoading(false);
        }).catch(e => {
            if (!mountedRef.current) return null
            setError(e.message);
            setLoading(false);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    },[currency, t]);

    useEffect(() => {
        setBreadcrumb([
            {
                to: "/",
                text: t('common.home'),
                active: false
            },
            {
                to: "/account/"+userData.currentAccount.id+"/",
                text: userData.currentAccount.name,
                active: false
            },
            {
                to: null,
                text: t('billing.title'),
                active: true
            }
        ]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    },[userData, setBreadcrumb, t]);

    useEffect(() => {
        getInvoices(userData.currentAccount.id, pageSize);
    },[pageSize, userData, getInvoices]);

    useEffect(() => {
        return () => {
            mountedRef.current = false
        }
    },[]);


    return (
        <Stack spacing={3}>
            {userData.currentAccount.owner === authUser.user.uid &&
                <Stack direction="row-reverse" spacing={1}>
                    <Button color="error" variant="outlined" onClick={() => navigate("/account/"+userData.currentAccount.id+"/billing/delete")}>{t('billing.cancel')}</Button>
                    {userData.currentAccount.price === 0 &&
                    <Button color="success" variant="contained" onClick={() => navigate("/account/"+userData.currentAccount.id+"/billing/plan")}>{t('billing.upgrade')}</Button>
                    }
                    {userData.currentAccount.price > 0 &&
                    <Button color="info" variant="contained" onClick={() => navigate("/account/"+userData.currentAccount.id+"/billing/payment-method")}>{t('billing.updatePaymentMethod')}</Button>
                    }
                </Stack>
            }
            <Card
                sx={{
                    backgroundColor: (theme) => theme.custom.card,
                    boxShadow: (theme) => theme.custom.cardShadow,
                    border: (theme) => `1px solid ${theme.custom.border}`,
                }}
            >
                    {loading?(
                        <Box p={3}>
                            <Loader text={t('billing.loadingHistory')}></Loader>
                        </Box>
                    ):(
                        <>
                        {error?(
                            <Box p={3}>
                                <Alert severity="error">{error}</Alert>
                            </Box>
                        ):(
                            <>
                                {total > 0 ? (
                                    <DataTable columns={[
                                        {name: t('billing.colInvoiceId'), field: "id", style: {width: '30%'}},
                                        {name: t('billing.colAmount'), field: "amountCol", style: {width: '15%'}},
                                        {name: t('billing.colStatus'), field: "statusCol", style: {width: '15%'}},
                                        {name: t('billing.colInvoiceDate'), field: "created", style: {width: '30%'}},
                                        {name: t('billing.colInvoiceUrl'), field: "urlCol", style: {width: '10%'}}
                                    ]}
                                    rows={rows}
                                    totalRows={total}
                                    pageSize={pageSize}
                                    page={page}
                                    handlePageChane={(e, p) => {
                                        if(p>page){
                                            getInvoices(userData.currentAccount.id, pageSize, 'next', qs.docs[qs.docs.length-1]);
                                        }
                                        if(p<page){
                                            getInvoices(userData.currentAccount.id, pageSize, 'previous', qs.docs[0]);
                                        }
                                        setPage(p);
                                    }}
                                    handlePageSizeChange={(e) => {
                                        setPageSize(e.target.value);
                                        setPage(0);
                                    }}
                                    ></DataTable>
                                ):(
                                    <Box p={3}><Typography>{t('billing.noInvoices')}</Typography></Box>
                                )}
                            </>
                        )}
                        </>
                    )}
            </Card>
        </Stack>
    )
}

export default PaymentList;
